import { OrgDashboardLabel } from "@/components/org/OrgDashboardLabel";
import {
  AccountMenuButton,
  AccountMenuItem,
  SCAppBar,
} from "@/components/sleep_checkup_v1/SCAppBar";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import { Divider, MenuItemProps, MenuList } from "@mui/material";
import Title from "@/components/Title";
import { facilityTheme } from "@/theme/themeV1";
import {
  SleepCheckupUserStore,
  callOrgLogoutAPI,
  fetchOrgMe,
} from "@/utils/auth";
import { getAxios, getOrgApiServerUrl, orgAxios } from "@/utils/axios";
import constants from "@/utils/constants";
import { Tab, Tabs, ThemeProvider } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import OrgMemberList from "./OrgMemberList";
import OrgProjectList from "./OrgProjectList";

function OrgAccountMenuList({
  onClickLogout,
}: {
  onClickLogout: MenuItemProps["onClick"];
}) {
  return (
    <MenuList>
      <AccountMenuItem
        startIcon={<MenuBookIcon />}
        onClick={() =>
          window
            .open(
              "https://drive.google.com/drive/folders/10HLhjbs89xHfl7KLNOB-ChUItrb6tzos",
              "_blank"
            )
            ?.focus()
        }
      >
        操作マニュアル
      </AccountMenuItem>
      <AccountMenuItem
        startIcon={<HelpOutlineOutlinedIcon />}
        onClick={() =>
          window
            .open("https://form.run/@slcp-dashboard-inquiry", "_blank")
            ?.focus()
        }
      >
        お問い合わせ
      </AccountMenuItem>
      <Divider />
      <AccountMenuItem startIcon={<LogoutIcon />} onClick={onClickLogout}>
        ログアウト
      </AccountMenuItem>
    </MenuList>
  );
}

// ---------------------------------------------------------------------------
// Context: refreshMe を子コンポーネントから呼べるようにする
// ---------------------------------------------------------------------------

const RefreshMeContext = createContext<() => void>(() => {});

export function useRefreshMe(): () => void {
  return useContext(RefreshMeContext);
}

function useCurrentTab(): number {
  const location = useLocation();
  if (location.pathname.startsWith("/org/members")) return 1;
  return 0;
}

export default function OrgLayout() {
  const [accountMenuOpen, setAccountMenuOpen] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState(
    () => SleepCheckupUserStore.getItem()?.role_code === "admin"
  );
  const navigate = useNavigate();
  const currentTab = useCurrentTab();

  const MIN_WIDTH = 1098;

  const user = SleepCheckupUserStore.getItem();

  const refreshMe = useCallback(async () => {
    try {
      await fetchOrgMe(orgAxios);
      const updated = SleepCheckupUserStore.getItem();
      setIsAdmin(updated?.role_code === "admin");
    } catch {
      // API失敗時は現状を維持
    }
  }, []);

  useEffect(() => {
    if (user == null) {
      navigate("/org/signin");
    } else {
      refreshMe();
    }
  }, [user, navigate, refreshMe]);

  const handleLogout = async () => {
    await callOrgLogoutAPI(getAxios(getOrgApiServerUrl()));
    navigate(constants.SIGNIN_URL_ORGANIZATION_USER);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    refreshMe();
    if (newValue === 0) navigate("/org/projects");
    if (newValue === 1) navigate("/org/members");
  };

  return (
    <ThemeProvider theme={facilityTheme}>
      <>
        <Title title="SLEEP COMPASS" />
        <meta
          name="description"
          content="睡眠健康測定 SLEEP COMPASSの組織用ダッシュボード"
        />
      </>
      <SCAppBar
        toolbarItem={<OrgDashboardLabel />}
        centerItem={
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              minHeight: 0,
              borderBottom: "none",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTabs-flexContainer": { gap: "32px", alignItems: "center" },
            }}
          >
            <Tab
              label="プロジェクト管理"
              sx={{
                minHeight: 0,
                py: 0,
                pb: "0 !important",
                fontSize: 16,
                fontWeight: "500 !important",
                minWidth: 0,
                px: 0,
                mr: "0 !important",
              }}
            />
            {isAdmin && (
              <Tab
                label="ユーザー管理"
                sx={{
                  minHeight: 0,
                  py: 0,
                  pb: "0 !important",
                  fontSize: 16,
                  fontWeight: "500 !important",
                  minWidth: 0,
                  px: 0,
                  mr: "0 !important",
                }}
              />
            )}
          </Tabs>
        }
        accountMenuButton={
          <AccountMenuButton
            accountMenuList={
              <OrgAccountMenuList onClickLogout={handleLogout} />
            }
            openMenu={accountMenuOpen}
            onClick={() => setAccountMenuOpen(true)}
            onClose={() => setAccountMenuOpen(false)}
          >
            {user?.username ?? ""}
          </AccountMenuButton>
        }
        minWidth={MIN_WIDTH}
      />
      <RefreshMeContext.Provider value={refreshMe}>
        <Routes>
          <Route path="projects" element={<OrgProjectList />} />
          <Route path="members" element={<OrgMemberList />} />
          <Route path="*" element={<OrgProjectList />} />
        </Routes>
      </RefreshMeContext.Provider>
    </ThemeProvider>
  );
}
